import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchPatientChat,
  markStaffChatRead,
  deleteStaffChatMessage,
  editStaffChatMessage,
  sendStaffChatMessage,
  UnauthorizedError,
  type ChatChannel,
  type ChatMessage,
} from "@/lib/clinician-api";
import { useAuth } from "@/auth/AuthContext";
import {
  COACH_DRAFT_LABEL,
  CHAT_ESCALATION_REASON,
  chatEscalationContext,
  isChatEscalation,
  type HandoffDto,
} from "@care-by-ella/contracts";
import { createHandoff, listPatientHandoffs } from "@/lib/handoff-api";
import { AiCoachDraftAssist, reportCoachDraftSent } from "./AiCoachDraftAssist";
import { API_BASE } from "@/lib/api";
import { TENANT } from "@/lib/tenant";
import { Avatar } from "@/components/Avatar";
import {
  SendToDoctorForm,
  type SendToDoctorOutcome,
} from "@/components/SendToDoctorForm";
import { doctorComposerReplaced } from "@/lib/medical-question";
import { appendToComposer, removeDraftParts } from "@/lib/coach-draft-composer";
import { SendDoctorNotice } from "./SendDoctorNotice";
import { senderPortraitKey } from "@/lib/chat-sender-portrait";
import { hiddenWhenReadOnly } from "./SupportView";

// The tenant's coach persona labels the automated coach messages (no author).
// TES-485: a portrait is a key on our media platform, the same one the app
// renders; a missing upload or a load error falls back to the initial circle
// inside <Avatar>.
const portraitUrl = (mediaKey: string | undefined) =>
  mediaKey
    ? `${API_BASE}/public-api/v1/media/${encodeURIComponent(mediaKey)}`
    : undefined;
const COACH = {
  name: TENANT.coach.displayName,
  avatarUrl: portraitUrl(TENANT.coach.avatarMediaKey),
};
// TES-719 — an authored message is labelled with its author, not the doctor
// persona's name: with a second doctor that would be the wrong person. The
// portrait is the one the API resolved for that author (senderPortraitKey).
const senderIdentity = (
  m: ChatMessage,
): { name: string; avatarUrl: string | undefined } => {
  const isCoachMessage = m.senderType === "coach";
  const roleSlot = isCoachMessage
    ? TENANT.coach.avatarMediaKey
    : TENANT.doctor?.avatarMediaKey;
  const fallbackName = isCoachMessage ? COACH.name : "Din läkare";
  return {
    name: (m.senderId && m.senderDisplayName) || fallbackName,
    avatarUrl: portraitUrl(senderPortraitKey(m, roleSlot)),
  };
};

// CAR-17 — clinician-side chat pane embedded on the PatientDetail page.
// Polls the same endpoint the mobile app does (employee-api/v1/chat/...).
// Quick-action requests render as flagged cards with an "Open renewal flow"
// shortcut that defers the heavy wiring to the existing renewal review UI.

interface Props {
  patientId: string;
  onUnauthorized: () => void;
  /** Which channel to open on. Defaults to the viewer's own. */
  initialChannel?: ChatChannel;
  /**
   * TES-1189 — a suggested first message to put in the composer, unsent and
   * editable, labelled as a suggestion until it is sent or emptied. Only
   * used on the viewer's own channel.
   */
  initialDraft?: string;
  /**
   * TES-1352 — "Rätta i Patientminnet" on an entry an AI draft used: the
   * page opens its own Patientminne correction for that entry. Without it
   * the action is not offered.
   */
  onCorrectMemory?: (itemId: string) => void;
}

const CHANNEL_LABEL: Record<ChatChannel, string> = {
  doctor: "Läkare",
  coach: "Coach",
};

const POLL_INTERVAL_MS = 10_000;

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function quickActionLabel(action: string): string {
  switch (action) {
    case "renew_rx":
      return "Receptförnyelse";
    case "ask_results":
      return "Frågar om provsvar";
    case "side_effects":
      return "Rapporterar biverkningar";
    default:
      return action;
  }
}

// Chat split — the pane shows one channel at a time ("Läkare" / "Coach" tabs).
// The composer is enabled only on the channel matching the viewer's role:
// the server enforces the same lock, this just states it up front.
function PatientChatPanePanel({
  patientId,
  onUnauthorized,
  initialChannel,
  initialDraft,
  onCorrectMemory,
}: Props) {
  const navigate = useNavigate();
  const { claims } = useAuth();
  const ownChannel: ChatChannel = claims?.role === "coach" ? "coach" : "doctor";
  // TES-506 — the caller may say which channel the viewer asked for. The chat
  // list shows BOTH channels to a doctor, so clicking a coach conversation and
  // landing in the doctor thread showed her a different conversation from the
  // one she clicked. Defaults to the viewer's own channel, which is still the
  // only one she can write in.
  const [channel, setChannel] = useState<ChatChannel>(
    initialChannel ?? ownChannel,
  );
  const canWrite = channel === ownChannel;
  // A-02 — a medical risk signal spotted in the chat is
  // escalated MANUALLY to the doctor. Anyone who is not the doctor (an admin as
  // admin, the coach) can raise it; it is a Handoff to the doctor carrying the
  // chat_escalation marker, so the doctor's queue and this pane read the same
  // row. The pane shows the latest one: waiting, or "Bedömt av läkare".
  const canEscalate = claims?.role !== "doctor";
  const [escalation, setEscalation] = useState<HandoffDto | null>(null);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalateNote, setEscalateNote] = useState("");
  const [escalating, setEscalating] = useState(false);
  const [escalateError, setEscalateError] = useState<string | null>(null);
  // TES-784 — edit mode. The bubble being edited is held in state; the draft
  // lives in its own field so the composer for new messages does not lose
  // what the clinician has already started typing.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [rowError, setRowError] = useState<string | null>(null);
  // TES-986 (Ö-266) — "Skicka till läkare" on ONE patient message in the
  // coach chat (a reflection she shared arrives here as a message too). The
  // server turns it into a medical review case with her own words; the coach
  // does no triage and sees nothing of the case afterwards.
  const canSendToDoctor = claims?.role === "coach" && channel === "coach";
  const [sentToDoctor, setSentToDoctor] = useState<Set<string>>(new Set());
  // TES-1120 — the message whose "Skicka till läkare" form is open.
  const [sendingToDoctor, setSendingToDoctor] = useState<string | null>(null);
  function sentToDoctorDone(messageId: string, outcome: SendToDoctorOutcome) {
    setSentToDoctor((prev) => new Set(prev).add(messageId));
    setSendingToDoctor(null);
    setRowError(
      outcome === "sent_reply_failed"
        ? "Skickat till läkare, men standardsvaret kunde inte skickas. Skriv till patienten själv."
        : null,
    );
  }
  function sendToDoctorFailed(e: unknown) {
    if (e instanceof UnauthorizedError) {
      onUnauthorized();
      return;
    }
    setRowError("Kunde inte skicka till läkare. Försök igen.");
  }
  const loadEscalation = useCallback(async () => {
    try {
      // TES-651 — this line fetched the entire DOCTOR QUEUE (`?toRole=doctor`) to
      // find a single row for a single patient. A coach who opened the chat
      // thereby read every patient's escalation texts, decrypted. Now we ask for
      // just this patient's handoffs.
      const all = await listPatientHandoffs(patientId);
      const mine = all
        .filter((h) => isChatEscalation(h))
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      setEscalation(mine[0] ?? null);
    } catch {
      // Best-effort: the chat still renders without the escalation line.
    }
  }, [patientId]);
  useEffect(() => {
    void loadEscalation();
  }, [loadEscalation]);
  async function escalate() {
    if (escalating) return;
    setEscalating(true);
    setEscalateError(null);
    try {
      const created = await createHandoff(patientId, {
        toRole: "doctor",
        priority: "high",
        reason: CHAT_ESCALATION_REASON,
        context: chatEscalationContext(channel, escalateNote),
      });
      setEscalation(created);
      setEscalateOpen(false);
      setEscalateNote("");
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      setEscalateError("Kunde inte eskalera. Försök igen.");
    } finally {
      setEscalating(false);
    }
  }
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [composer, setComposer] = useState(
    initialDraft && canWrite ? initialDraft : "",
  );
  // TES-1189 — A-01: a draft is "alltid märkt" until it leaves. Shown while
  // the composer holds text that came from the draft and has not been sent.
  const [fromDraft, setFromDraft] = useState(Boolean(initialDraft && canWrite));
  const sectionRef = useRef<HTMLElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  // The welcome link lands on the patient page; bring the chat and its
  // draft into view rather than leaving the coach to scroll for it.
  useEffect(() => {
    if (!initialDraft) return;
    sectionRef.current?.scrollIntoView({ block: "start" });
    composerRef.current?.focus({ preventScroll: true });
  }, [initialDraft]);
  const [sending, setSending] = useState(false);
  // TES-1237 — the AI suggestion currently in the composer (QA only), so the
  // send can report whether the coach changed it.
  const [aiDraft, setAiDraft] = useState<{
    id: string;
    original: string;
  } | null>(null);
  // TES-1351 — Emelie's test of "Lägg till i svaret" saw nothing happen: the
  // text was appended below the fold of a two-row composer that neither grew
  // nor scrolled. After the AI writes into the composer, it is focused with
  // the caret and the view at the end, so the addition is visible.
  const revealComposerEnd = useCallback(() => {
    requestAnimationFrame(() => {
      const el = composerRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      el.setSelectionRange(el.value.length, el.value.length);
      el.scrollTop = el.scrollHeight;
    });
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  // TES-1145 — from the thread's own read, refreshed on every poll.
  const [doctorChatClosed, setDoctorChatClosed] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [historyExhausted, setHistoryExhausted] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  // TES-1237 — her current message: the newest in the thread, if it is hers.
  const newest = messages.at(-1);
  const latestPatientMessage =
    newest &&
    newest.senderType === "patient" &&
    newest.kind === "text" &&
    !newest.deletedAt
      ? newest
      : null;
  const composerReplaced = doctorComposerReplaced({
    role: claims?.role,
    channel,
    loaded,
    messageCount: messages.length,
    doctorChatClosed,
  });

  async function loadOlder() {
    const oldest = messages[0]?.createdAt;
    if (!oldest || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const data = await fetchPatientChat(
        patientId,
        channel,
        undefined,
        oldest,
      );
      if (data.messages.length < 50) setHistoryExhausted(true);
      if (data.messages.length > 0) {
        setMessages((prev) => {
          const known = new Set(prev.map((m) => m.id));
          return [...data.messages.filter((m) => !known.has(m.id)), ...prev];
        });
      }
    } catch {
      // best-effort — the link stays for a retry
    } finally {
      setLoadingOlder(false);
    }
  }

  // CAR-62: refs for state the poll loop reads on each tick but should NOT
  // re-arm on. Two bugs lived here:
  //   1. `newestCreatedAt` was captured into the effect closure at mount,
  //      so the interval kept asking for the full message log instead of
  //      the delta — defeated the since-cursor entirely.
  //   2. `load` depended on `onUnauthorized`, which the parent
  //      (PatientDetail) passes as a fresh arrow per render. Every parent
  //      re-render rebuilt `load` → tore down + re-armed the interval, so
  //      a request in flight could land after unmount and the duplicate
  //      re-arming masked the stale-cursor bug.
  // Stashing both in refs lets the effect deps drop to [patientId] only.
  const newestRef = useRef<string | null>(null);
  useEffect(() => {
    newestRef.current = messages[messages.length - 1]?.createdAt ?? null;
  }, [messages]);

  const onUnauthorizedRef = useRef(onUnauthorized);
  useEffect(() => {
    onUnauthorizedRef.current = onUnauthorized;
  }, [onUnauthorized]);

  // TES-784 — redigera / radera klinikerns EGNA meddelanden.
  //
  // Both are followed by a FULL reload, not an incremental one. Since
  // TES-785 the delta does carry changes too, but a full reload here shows
  // the result immediately instead of waiting out the next poll tick.
  const applyEdit = useCallback(
    async (messageId: string) => {
      const body = editDraft.trim();
      if (!body) return;
      setRowError(null);
      try {
        await editStaffChatMessage(patientId, messageId, body);
        setEditingId(null);
        setEditDraft("");
        await loadRef.current(undefined);
      } catch {
        setRowError("Ändringen kunde inte sparas.");
      }
    },
    [patientId, editDraft],
  );

  const removeMessage = useCallback(
    async (messageId: string) => {
      // Soft delete, but the patient sees it — hence a confirmation.
      if (
        !window.confirm(
          "Ta bort meddelandet? Texten döljs för patienten. Att det togs bort syns fortfarande.",
        )
      ) {
        return;
      }
      setRowError(null);
      try {
        await deleteStaffChatMessage(patientId, messageId);
        if (editingId === messageId) {
          setEditingId(null);
          setEditDraft("");
        }
        await loadRef.current(undefined);
      } catch {
        setRowError("Meddelandet kunde inte tas bort.");
      }
    },
    [patientId, editingId],
  );

  const load = useCallback(
    async (since?: string) => {
      try {
        const data = await fetchPatientChat(patientId, channel, since);
        setDoctorChatClosed(data.doctorChatClosed === true);
        if (!since) {
          // Initial load — full replace.
          setMessages(data.messages);
          // Mark every prior message read once the pane mounts.
          try {
            await markStaffChatRead(patientId, channel);
          } catch {
            // Read receipts are best-effort.
          }
        } else if (data.messages.length > 0) {
          setMessages((prev) => {
            // TES-785: the delta now carries edits and deletes too, so a
            // known id is overwritten instead of skipped. An unknown id that
            // is OLDER than the cursor is a change outside the loaded window
            // — it would land last and in the wrong order.
            const index = new Map(prev.map((m, i) => [m.id, i]));
            const merged = [...prev];
            for (const m of data.messages) {
              const at = index.get(m.id);
              if (at !== undefined) merged[at] = m;
              else if (m.createdAt > since) merged.push(m);
            }
            return merged;
          });
        }
        setLoaded(true);
      } catch (e) {
        if (e instanceof UnauthorizedError) {
          onUnauthorizedRef.current();
          return;
        }
        // Polling is best-effort; only surface initial-load errors.
        if (!since) setError(e instanceof Error ? e.message : String(e));
      }
    },
    [patientId, channel],
  );

  // TES-784 — a stable reference so edit/delete can reload without depending
  // on `load` and thereby being recreated on every new row.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  useEffect(() => {
    setMessages([]);
    setLoaded(false);
    setHistoryExhausted(false);
    void load();
    const handle = setInterval(() => {
      // Read the *live* cursor via the ref so the interval polls for the
      // delta since the latest known message, not the value captured at
      // effect-mount (which was always null).
      void load(newestRef.current ?? undefined);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [load]);

  // Auto-scroll on new message.
  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages.length]);

  async function send() {
    const trimmed = composer.trim();
    if (!trimmed) return;
    setSending(true);
    setError(null);
    try {
      await sendStaffChatMessage(patientId, channel, trimmed);
      if (aiDraft) reportCoachDraftSent(patientId, aiDraft, trimmed);
      setAiDraft(null);
      setComposer("");
      setFromDraft(false);
      // Pull our own message back via the standard poll path so the rendering
      // stays a single source of truth (server is authoritative on createdAt).
      await load(newestRef.current ?? undefined);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorizedRef.current();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  function openRenewalFlow() {
    // TES-290 — the renewal decision loop now lives in the work-queue
    // workspace: the doctor's `renewals` lane embeds approve/reject/dose/note
    // in place. Route the doctor there instead of the old scroll-to-anchor stub.
    navigate("/work-queue");
  }

  return (
    <section
      ref={sectionRef}
      id="chat"
      className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col"
      style={{ minHeight: 480 }}
    >
      <header className="flex items-center justify-between mb-sm">
        <div className="flex items-center gap-md">
          <h2 className="font-display text-lg">Chatt</h2>
          <div className="flex gap-xs">
            {(Object.keys(CHANNEL_LABEL) as ChatChannel[]).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setChannel(c)}
                className={
                  c === channel
                    ? "px-sm py-xs rounded-pill text-xs bg-brand text-white"
                    : "px-sm py-xs rounded-pill text-xs bg-surfaceMuted text-textSecondary"
                }
              >
                {CHANNEL_LABEL[c]}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-md">
          {canEscalate ? (
            <button
              type="button"
              onClick={() => setEscalateOpen((o) => !o)}
              className="px-sm py-xs rounded-pill text-xs border border-danger text-danger hover:bg-surfaceMuted"
            >
              Eskalera till läkare
            </button>
          ) : null}
          <span className="text-xs text-textSecondary">
            {messages.length} meddelanden
          </span>
        </div>
      </header>

      {/* A-02 — the escalation's five fields, read straight off the handoff. */}
      {escalation ? (
        <p
          className={
            escalation.status === "resolved"
              ? "text-xs text-textSecondary mb-sm"
              : "text-xs text-danger mb-sm"
          }
        >
          {escalation.status === "resolved"
            ? `Bedömt av läkare ${new Date(escalation.resolvedAt ?? escalation.updatedAt).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })}`
            : `Eskalerad till läkare ${new Date(escalation.createdAt).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })} · ${escalation.status === "sent" ? "väntar på läkaren" : "läkaren har tagit den"}`}
        </p>
      ) : null}
      {escalateOpen ? (
        <div className="border border-danger rounded-md p-sm mb-sm flex flex-col gap-xs">
          <label
            className="text-xs text-textSecondary"
            htmlFor="chat-escalation-note"
          >
            Vad i chatten behöver läkaren bedöma? (valfritt)
          </label>
          <textarea
            id="chat-escalation-note"
            value={escalateNote}
            onChange={(e) => setEscalateNote(e.target.value)}
            rows={2}
            className="border border-surfaceMuted rounded-md p-xs text-sm"
          />
          {escalateError ? (
            <p className="text-xs text-danger">{escalateError}</p>
          ) : null}
          <div className="flex gap-sm">
            <button
              type="button"
              onClick={() => void escalate()}
              disabled={escalating}
              className="px-md py-xs rounded-md bg-danger text-white text-sm disabled:opacity-50"
            >
              {escalating ? "Eskalerar…" : "Eskalera till läkare"}
            </button>
            <button
              type="button"
              onClick={() => setEscalateOpen(false)}
              className="px-md py-xs rounded-md bg-surfaceMuted text-sm"
            >
              Avbryt
            </button>
          </div>
        </div>
      ) : null}

      <div
        ref={listRef}
        className="flex-1 overflow-y-auto bg-background rounded-sm p-sm flex flex-col gap-sm"
        style={{ maxHeight: 420, minHeight: 240 }}
      >
        {loaded && messages.length >= 50 && !historyExhausted ? (
          <button
            type="button"
            onClick={() => void loadOlder()}
            disabled={loadingOlder}
            className="self-center text-xs text-textSecondary underline"
          >
            {loadingOlder ? "Hämtar…" : "Visa äldre meddelanden"}
          </button>
        ) : null}
        {!loaded ? (
          <p className="text-textSecondary text-sm">Hämtar…</p>
        ) : messages.length === 0 ? (
          <p className="text-textSecondary text-sm">Inga meddelanden än.</p>
        ) : (
          messages.map((m) => {
            if (m.senderType === "system" || m.kind === "system_event") {
              return (
                <div
                  key={m.id}
                  className="self-center text-xs text-textSecondary"
                >
                  {m.body}
                </div>
              );
            }
            const isDoctor = m.senderType === "doctor";
            // CAR-121 — the automated coach persona. Read-only here: it renders
            // as a left-aligned non-doctor bubble (same side as the patient is
            // not — i.e. the clinic side) with a small "Coach" tag so staff can
            // tell an automated coach message from a patient message at a glance.
            const isCoach = m.senderType === "coach";
            if (m.kind === "quick_action_request") {
              return (
                <div
                  key={m.id}
                  className="self-start max-w-[80%] border border-warning/40 bg-warning/5 rounded-md p-sm"
                >
                  <div className="flex items-center gap-xs text-xs uppercase tracking-wide text-warning font-medium">
                    <span>Snabbval</span>
                    <span>·</span>
                    <span>
                      {quickActionLabel(m.payload?.action ?? "unknown")}
                    </span>
                  </div>
                  <p className="text-sm text-textPrimary mt-xs">{m.body}</p>
                  <div className="flex justify-between items-center mt-xs">
                    <span className="text-xs text-textSecondary">
                      {fmtTime(m.createdAt)}
                    </span>
                    {m.payload?.action === "renew_rx" ? (
                      <button
                        type="button"
                        onClick={openRenewalFlow}
                        className="text-xs text-brand hover:underline"
                      >
                        Öppna förnyelseflödet →
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            }
            // Show a sender avatar for the two clinic-side personas (doctor +
            // coach). Patient messages stay avatar-less so the patient's own
            // words read as the "default" voice.
            // TES-719 — a message written by a person shows that person: the
            // name the API sends with it and their own portrait. The tenant
            // persona is only for messages without an author (the automated
            // coach journey); with two doctors it named Kirill on both.
            const persona = isDoctor || isCoach ? senderIdentity(m) : null;
            return (
              <div
                key={m.id}
                className={`flex items-end gap-xs max-w-[80%] ${
                  isDoctor ? "self-end flex-row-reverse" : "self-start"
                }`}
              >
                {persona ? (
                  <Avatar
                    name={persona.name}
                    src={persona.avatarUrl}
                    toneClassName={
                      // Persona colors match the mobile app (Avatar.tsx):
                      // coach = brand violet, doctor = warm.
                      isDoctor
                        ? "bg-warning/15 text-warning"
                        : "bg-brand/20 text-brand"
                    }
                  />
                ) : null}
                <div
                  className={`rounded-md p-sm ${
                    isDoctor
                      ? "bg-brand text-textOnBrand"
                      : "bg-surface border border-surfaceMuted"
                  }`}
                >
                  {isCoach && persona ? (
                    <span className="text-xs uppercase tracking-wide text-success font-medium">
                      {persona.name}
                    </span>
                  ) : null}
                  {/* TES-784 — editing in the bubble. The clinician sees the
                      text where it stands instead of in a modal that removes
                      the thread she is writing in relation to. */}
                  {editingId === m.id ? (
                    <div className="flex flex-col gap-xs">
                      <textarea
                        id={`chat-edit-${m.id}`}
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        rows={3}
                        className="w-full rounded-md border border-surfaceMuted bg-surface p-xs text-sm text-textPrimary"
                        aria-label="Redigera meddelandet"
                      />
                      <div className="flex gap-xs">
                        <button
                          type="button"
                          onClick={() => void applyEdit(m.id)}
                          disabled={!editDraft.trim()}
                          className="rounded-md bg-brand px-sm py-[2px] text-xs text-textOnBrand disabled:opacity-50"
                        >
                          Spara
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(null);
                            setEditDraft("");
                          }}
                          className="rounded-md border border-surfaceMuted px-sm py-[2px] text-xs"
                        >
                          Avbryt
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                  )}
                  <p
                    className={`text-xs mt-xs ${
                      isDoctor ? "text-textOnBrand/70" : "text-textSecondary"
                    }`}
                  >
                    {/* TES-758 (Ö-171 b) — "Redigerat" is the safeguard that
                        replaced immutability. Without the marking an edited
                        text cannot be told apart from one that always read
                        that way, and clinicians read the thread as a basis
                        for decisions. */}
                    {fmtTime(m.createdAt)}
                    {m.editedAt ? " · Redigerat" : ""}
                  </p>
                  {/* TES-784 — the actions are shown only on the clinician's
                      OWN text messages that have not already been removed.
                      The service rejects everything else (403 / 400 / 409),
                      and a surface that offers something the server refuses
                      is a surface that lies. */}
                  {canSendToDoctor &&
                  m.senderType === "patient" &&
                  m.kind === "text" &&
                  !m.deletedAt ? (
                    <div className="mt-xs">
                      {sentToDoctor.has(m.id) ? (
                        <span className="text-xs text-textSecondary">
                          Skickat till läkare
                        </span>
                      ) : sendingToDoctor === m.id ? (
                        <SendToDoctorForm
                          patientId={patientId}
                          messageId={m.id}
                          onDone={(outcome) => sentToDoctorDone(m.id, outcome)}
                          onCancel={() => setSendingToDoctor(null)}
                          onError={sendToDoctorFailed}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setRowError(null);
                            setSendingToDoctor(m.id);
                          }}
                          disabled={sendingToDoctor !== null}
                          className="text-xs text-textSecondary underline disabled:opacity-50"
                        >
                          Skicka till läkare
                        </button>
                      )}
                    </div>
                  ) : null}
                  {m.senderType === ownChannel &&
                  m.kind === "text" &&
                  !m.deletedAt &&
                  editingId !== m.id ? (
                    <div className="mt-xs flex gap-sm">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(m.id);
                          setEditDraft(m.body);
                        }}
                        className={`text-xs underline ${
                          isDoctor
                            ? "text-textOnBrand/70"
                            : "text-textSecondary"
                        }`}
                      >
                        Redigera
                      </button>
                      <button
                        type="button"
                        onClick={() => void removeMessage(m.id)}
                        className={`text-xs underline ${
                          isDoctor
                            ? "text-textOnBrand/70"
                            : "text-textSecondary"
                        }`}
                      >
                        Ta bort
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      {error ? <p className="text-danger text-xs mt-xs">{error}</p> : null}
      {/* TES-784 — a failed edit/delete must be visible. Without this line
          a clinician only sees that nothing happened. */}
      {rowError ? (
        <p className="text-danger text-xs mt-xs">{rowError}</p>
      ) : null}

      {!canWrite ? (
        <p className="text-xs text-textSecondary mt-sm">
          {channel === "coach"
            ? "Coach-kanalen besvaras av coach — du läser den här."
            : "Läkarkanalen besvaras av läkare — du läser den här."}
        </p>
      ) : null}

      {/* TES-1147 — no doctor-chat history means no doctor chat: the patient
          has no doctor thread in her app, and the server refuses a first
          free-text message. The doctor sends a notice (and a task) instead.
          TES-1145 — the same once the doctor chat is closed for her app,
          history or not: the earlier thread stays above, read only. */}
      {composerReplaced ? (
        <div className="mt-sm flex flex-col gap-sm">
          <p className="text-xs text-textSecondary">
            Patienten har ingen läkarchatt. Nya besked från dig publiceras under
            Din vård, och behöver hon göra något blir det en uppgift i appen.
          </p>
          <SendDoctorNotice
            patientId={patientId}
            onUnauthorized={() => onUnauthorizedRef.current()}
          />
        </div>
      ) : (
        <>
          {/* TES-1237 — "Föreslå svar" on her newest message, while it is
              still the last word in the coach chat. QA only: the component
              renders nothing unless the server has the feature on. */}
          {claims?.role === "coach" &&
          channel === "coach" &&
          canWrite &&
          latestPatientMessage ? (
            <AiCoachDraftAssist
              patientId={patientId}
              caseKind="patient_message"
              messageId={latestPatientMessage.id}
              buttonLabel="Föreslå svar"
              activeDraftId={aiDraft?.id ?? null}
              onDraft={(d) => {
                setAiDraft(d);
                // Whatever the coach already wrote stays; the draft follows.
                setComposer((c) => appendToComposer(c, d.original));
                setFromDraft(true);
                revealComposerEnd();
              }}
              onAppend={(t) => {
                setComposer((c) => appendToComposer(c, t));
                revealComposerEnd();
              }}
              onDiscard={(parts) => {
                setAiDraft(null);
                setComposer((c) => removeDraftParts(c, parts));
                setFromDraft(false);
              }}
              onCorrectMemory={onCorrectMemory}
              onEscalate={(id) => {
                setRowError(null);
                setSendingToDoctor(id);
              }}
              onUnauthorized={() => onUnauthorizedRef.current()}
            />
          ) : null}
          {fromDraft && canWrite && composer.trim() ? (
            <p className="text-xs text-textSecondary mt-sm">
              {COACH_DRAFT_LABEL}
            </p>
          ) : null}
          <div className="flex gap-sm mt-sm">
            <textarea
              ref={composerRef}
              value={composer}
              onChange={(e) => setComposer(e.target.value)}
              placeholder="Skriv ett meddelande till patienten…"
              disabled={sending || !canWrite}
              // A whole AI draft needs room to be read before it is sent.
              rows={aiDraft ? 8 : 2}
              onKeyDown={(e) => {
                // Cmd/Ctrl + Enter sends — same shortcut the approve button uses.
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                  e.preventDefault();
                  void send();
                }
              }}
              className="flex-1 border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand"
            />
            <button
              type="button"
              onClick={() => void send()}
              disabled={sending || !canWrite || composer.trim().length === 0}
              className="px-md py-sm rounded-md bg-brand text-textOnBrand text-sm hover:opacity-90 disabled:opacity-60 self-end"
            >
              {sending ? "Skickar…" : "Skicka"}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

// TES-1040 — hidden in the admin's read-only support view: chat is out of scope for the support views (the API keeps every chat
// route doctor/coach-only).
export const PatientChatPane = hiddenWhenReadOnly(PatientChatPanePanel);
